import type { Mode } from '@/types/enums/mode'

import { forwardRef, type ReactNode, useMemo } from 'react'

import LoadingSpinner from '@/components/atomic/loading-spinner'
import { BodySmall, BodySubtext } from '@/components/atomic/text'
import { cn } from '@/utils/shadcn/utils'

import {
  Button as BaseButton,
  type ButtonProps as BaseButtonProps,
} from './base'
import { ButtonSize, ButtonSizePx, ButtonVariant } from './variants'

// Re-export the enum directly
export { ButtonVariant }

export interface ButtonProps extends BaseButtonProps {
  icon?: ReactNode
  iconSide?: 'left' | 'right'
  label?: string | ReactNode
  loadingText?: string
  variant: ButtonVariant
  isLoading?: boolean
  truncate?: boolean
  mode?: Mode // for Storybook to control dark/light mode for testing
}

/**
 * @deprecated Use `Button` from `@moment/design-system-ui/button` instead.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      icon,
      iconSide,
      label,
      size = ButtonSize.Small,
      isLoading,
      loadingText,
      truncate,
      variant = ButtonVariant.Primary, // Default to primary
      mode,
      ...props
    },
    ref,
  ) => {
    // For icon-only button (no label)
    const iconOnly = !label && !isLoading && icon

    const sizeClass = useMemo(() => {
      switch (size) {
        case ButtonSize.XSmall:
          return `h-[${ButtonSizePx.XSmall}px] w-[${ButtonSizePx.XSmall}px]`
        case ButtonSize.Small:
          return `h-[${ButtonSizePx.Small}px] w-[${ButtonSizePx.Small}px]`
        case ButtonSize.Large:
          return `h-[${ButtonSizePx.Large}px] w-[${ButtonSizePx.Large}px]`
      }
    }, [size])

    if (iconOnly) {
      return (
        <BaseButton
          ref={ref}
          className={cn(`px-0.5 py-0.5 ${sizeClass}`, className)}
          variant={variant}
          {...props}
        >
          {icon}
        </BaseButton>
      )
    }

    // For loading state
    let displayIcon = icon
    let displayLabel = label
    let displayIconSide = iconSide

    if (isLoading) {
      // Pass the variant to the spinner
      displayIcon = (
        <LoadingSpinner
          variant={variant}
          size={size}
          disabled={props.disabled}
          mode={mode}
        />
      )
      displayIconSide = 'left'

      if (loadingText) {
        displayLabel = loadingText
      }
    }

    // For loading spinner only (no label)
    if (isLoading && !displayLabel) {
      return (
        <BaseButton
          ref={ref}
          className={cn('flex items-center justify-center', className)}
          variant={variant}
          {...props}
          size={size}
        >
          <LoadingSpinner
            variant={variant}
            size={size}
            disabled={props.disabled}
            mode={mode}
          />
        </BaseButton>
      )
    }

    // Default case: button with icon and/or label
    return (
      <BaseButton
        ref={ref}
        variant={variant}
        {...props}
        // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        size={variant === ButtonVariant.Badge ? ('badge' as any) : size}
        className={cn('flex items-center', className)}
      >
        {displayIcon && displayIconSide === 'left' && (
          <div
            className={
              displayLabel
                ? // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
                  variant === ButtonVariant.Badge
                  ? 'mr-1.5'
                  : 'mr-2'
                : ''
            }
          >
            {displayIcon}
          </div>
        )}

        {displayLabel &&
          (typeof displayLabel === 'string' ? (
            // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
            size === 'small' ? (
              // Leading of 85% removes vertical padding inside the text box, ensuring text aligns by cap height
              // (the height of capital letters). This creates more stable vertical alignment, particularly
              // when zooming in/out, preventing the common issue of text appearing vertically misaligned.
              // BUT we don't want to cut parts of text below cap height (descenders like in 'g', 'j', 'p'),
              // so we add 'py-1' to create extra space that contains these descenders while maintaining truncation.
              // OVERALL: This is a workaround for broken 'Inter' font metadata. Ideally, we'd do an overhaul of type
              // on the app, and import 'Inter' locally to adjust it ourselves. - @Vova
              <BodySubtext
                className={cn('leading-[85%] py-1', truncate ? 'truncate' : '')}
              >
                {displayLabel}
              </BodySubtext>
            ) : (
              <BodySmall className={cn(truncate ? 'truncate' : '')}>
                {displayLabel}
              </BodySmall>
            )
          ) : (
            // For ReactNode labels, render directly with appropriate text sizing
            <div
              className={cn(
                size === 'small' ? 'text-xs leading-[85%] py-1' : 'text-sm',
                truncate ? 'truncate' : '',
              )}
            >
              {displayLabel}
            </div>
          ))}

        {displayIcon && displayIconSide === 'right' && (
          <div className={variant === ButtonVariant.Badge ? 'ml-1.5' : 'ml-2'}>
            {displayIcon}
          </div>
        )}
      </BaseButton>
    )
  },
)

Button.displayName = 'Button'

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default Button
